import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import kebabCase from 'lodash/kebabCase'
import { BLOG_TAGS, TAG_CUSTOMER_STORIES } from '@anvilco-pkg/sanity'
import styled from 'styled-components'

import useAttachPageEventHandlers from 'hooks/useAttachPageEventHandlers'
import useAnalytics from 'lib/analytics/useAnalytics'
import {
  EVENT_OUTBOUND_LINK_CLICKED,
  EVENT_MAILTO_LINK_CLICKED,
} from 'lib/analytics/events'
import { normalizeBlogPost } from 'lib/content'

import media from 'media'

import Container from 'components/Container'
import ArrowLink from 'components/ArrowLink'
import Section from 'components/page/Section'
import HubspotForm from 'components/hubspot/HubspotForm'

import ArticleHelmet from 'components/Article/Helmet'
import ContactAnvil from 'components/Signup/ContactAnvil'
import Layout from 'components/page/layouts/GatsbyLayout'

import { contentByBreakpoint } from 'helpers'
import { getBlogPostingSchema } from 'helpers/html-schema'

import Header from './components/Header'
import Body from './components/Body'

import renderNavbar from 'helpers/renderNavbar'
import { getPagePath } from 'helpers/path'
import { BLOG_SLUG } from 'consts'

// TODO: CANDYCODE migrate to next.js

const StyledArrowLink = styled(ArrowLink)`
  margin-top: 40px;

  ${media.desktop`
    margin: 0;
  `}
`

const Subscribe = styled(Container)`
  h2 {
    font-size: 24px;
  }

  p {
    margin: 20px 0;
  }
`

const StyledSection = styled(Section)`
  &&& {
    overflow: unset;
  }

  @media (max-width: 1439px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 767px) {
    padding-top: 0;
  }
`

const renderSignup = () => {
  return <ContactAnvil />
}

export default function Template ({
  location: { pathname },
  data: {
    markdownRemark,
    sanityBlogPost,
    sanityPodcastPage,
    titleImage,
    descriptionImage,
    svgAsPngBlogCover,
    sanityAuthorDocument,
    airtableRecord,
  },
  pageContext: { selectedTag, canonicalTag },
}) {
  const {
    title,
    metaTitle,
    socialTitle,
    summary,
    metaDescription,
    author,
    heroImage,
    content,
    keywords,
    modifiedDate,
    publishDate,
    slug,
    industry,
    video,
    additionalResources,
  } = markdownRemark
    ? normalizeBlogPost(markdownRemark, {
        sanityHeroImage: titleImage || descriptionImage,
        sanityAuthorDocument,
        airtableRecord: airtableRecord?.data,
      })
    : sanityPodcastPage
      ? normalizeBlogPost(sanityPodcastPage)
      : normalizeBlogPost(sanityBlogPost, {
          airtableRecord: airtableRecord?.data,
        })

  const isCaseStudy = BLOG_TAGS.some(
    ({ key, value }) =>
      value.toLowerCase() === selectedTag.toLowerCase() &&
      key === TAG_CUSTOMER_STORIES
  )
  const publicImageURL = svgAsPngBlogCover?.imageUrl || heroImage?.publicURL

  const mailToClickHandler = useAnalytics({
    eventName: EVENT_MAILTO_LINK_CLICKED,
  })
  const outboundLinkClickHandler = useAnalytics({
    eventName: EVENT_OUTBOUND_LINK_CLICKED,
  })

  useAttachPageEventHandlers({
    selectors: ['a[href^="http"]', 'a[href^="https"]'],
    event: 'click',
    eventHandler: (e) => {
      outboundLinkClickHandler({
        click_url: e?.target?.href,
        action: 'Clicked',
        link_text: e.target.innerText,
      })
    },
  })
  useAttachPageEventHandlers({
    selectors: ['a[href^="mailto:"]'],
    event: 'click',
    eventHandler: (e) => {
      mailToClickHandler({
        click_url: e?.target?.href,
        action: 'Clicked',
        link_text: e.target.innerText,
      })
    },
  })

  return (
    <Layout>
      <ArticleHelmet
        title={metaTitle || title}
        meta={{
          description: metaDescription || summary.plainText,
          keywords,
          socialTagline: socialTitle || metaTitle || title,
        }}
        socialImageURL={publicImageURL}
        pathname={pathname}
        canonicalPathname={getPagePath({
          prefixSlug: BLOG_SLUG,
          tagSlug: kebabCase(canonicalTag),
          pageSlug: slug,
        })}
        schema={getBlogPostingSchema({
          title,
          author,
          dateModified: modifiedDate?.toISOString(),
          datePublished: publishDate?.toISOString(),
          summary,
          imageURL: publicImageURL,
        })}
        publishedDate={publishDate?.toISOString()}
        modifiedDate={modifiedDate?.toISOString()}
      />
      {renderNavbar()}
      <StyledSection>
        <Header
          title={title}
          author={author}
          date={!isCaseStudy && modifiedDate.format('MMMM DD, YYYY')}
          summary={summary}
          image={heroImage}
          path={pathname}
          selectedTag={selectedTag}
          video={video}
        />
        <Body
          title={title}
          summary={summary}
          path={pathname}
          content={content}
          additionalResources={additionalResources}
          renderBottomAction={() => (
            <div>
              {industry && (
                <StyledArrowLink
                  direction="left"
                  width={100}
                  to={`/solutions/${kebabCase(industry)}/`}
                >
                  {contentByBreakpoint({
                    mobile: `${industry} solutions`,
                    tablet: `Other ${industry} solutions`,
                  })}
                </StyledArrowLink>
              )}
              <StyledArrowLink direction="left" width={100} to="/blog">
                {contentByBreakpoint({
                  mobile: 'View all',
                  tablet: 'View all articles',
                })}
              </StyledArrowLink>
            </div>
          )}
        />
        <Subscribe>
          <h2>Subscribe to the Anvil blog</h2>
          <p>Get new stories like this delivered directly to your inbox.</p>
          <HubspotForm formName="Blog Subscription" />
        </Subscribe>
      </StyledSection>
      {renderSignup()}
    </Layout>
  )
}

export const pageQuery = graphql`
  query BlogPostByPath(
    $id: String
    $title: String
    $authorId: String
    $airtableId: String
  ) {
    # TODO: titleImage and descriptionImage should use airtableId instead, so content authors can freely
    # change the title and description of the blog post without having to update the image in Sanity
    titleImage: sanityImageAsset(title: { eq: $title }) {
      gatsbyImageData
      url
      title
    }
    descriptionImage: sanityImageAsset(description: { eq: $title }) {
      gatsbyImageData
      url
      description
    }
    svgAsPngBlogCover(title: { eq: $title }) {
      imageUrl
    }
    markdownRemark(id: { eq: $id }) {
      id
      htmlAst
      frontmatter {
        date
        slug
        title
        keywords
        company
        tags
        author
        type
        summary
        industry
        metaTitle
        socialTitle
        metaDescription
        airtableId
      }
      fields {
        dateLastModified
      }
      internal {
        type
      }
    }
    sanityAuthorDocument(_id: { eq: $authorId }) {
      _id
      id
      person {
        firstName
        lastName
        image {
          asset {
            gatsbyImageData
            altText
          }
        }
      }
      github
    }
    sanityBlogPost(id: { eq: $id }) {
      id
      # NOTE: we can only use _rawContent instead of content.children
      # because we need markDefs on all spans, and for whatever reason it
      # only comes in through _rawContent.
      # resolveReferences is for internal links (reference fields in sanity)
      # that we would rather have the actual data for - not the reference ID
      _rawContent(resolveReferences: { maxDepth: 10 })
      _rawArticle(resolveReferences: { maxDepth: 10 })
      article {
        slug
        tags
        title
        seoData {
          metaTitle
          socialTitle
          metaDescription
        }
        keywords
        airtableId
      }
      author {
        _id
        id
        person {
          firstName
          lastName
          jobTitle
          organization {
            organizationName
            logo {
              asset {
                gatsbyImageData
              }
            }
          }
          image {
            asset {
              gatsbyImageData
              altText
            }
          }
        }
        github
      }
      _id
      id
      internal {
        type
      }
      heroImage {
        asset {
          gatsbyImageData
          url
        }
      }
      additionalResources {
        __typename
        ... on SanityAskAnvil {
          article {
            title
            _rawSummary(resolveReferences: { maxDepth: 10 })
            previewTitle
            _rawPreviewSummary(resolveReferences: { maxDepth: 10 })
            tags
            slug
          }
        }
        ... on SanityBlogPost {
          article {
            title
            _rawSummary(resolveReferences: { maxDepth: 10 })
            previewTitle
            _rawPreviewSummary(resolveReferences: { maxDepth: 10 })
            tags
            slug
          }
          heroImage {
            asset {
              gatsbyImageData
              url
            }
          }
        }
        ... on SanityPodcastPage {
          podcast {
            title
            _rawSummary(resolveReferences: { maxDepth: 10 })
            previewTitle
            _rawPreviewSummary(resolveReferences: { maxDepth: 10 })
            coverImage {
              asset {
                gatsbyImageData
                altText
                url
              }
            }
          }
        }
      }
    }
    sanityPodcastPage(id: { eq: $id }) {
      id
      seoData {
        metaTitle
        socialTitle
        metaDescription
      }
      podcast {
        title
        _rawSummary(resolveReferences: { maxDepth: 10 })
        _rawTranscript(resolveReferences: { maxDepth: 10 })
        video {
          videoId
          title
        }
        coverImage {
          asset {
            gatsbyImageData
            altText
            url
          }
        }
        guest {
          _id
          github
          person {
            firstName
            lastName
            image {
              asset {
                gatsbyImageData
                altText
              }
            }
          }
        }
        publishedDate
      }
      additionalResources {
        __typename
        ... on SanityAskAnvil {
          article {
            title
            _rawSummary(resolveReferences: { maxDepth: 10 })
            previewTitle
            _rawPreviewSummary(resolveReferences: { maxDepth: 10 })
            tags
            slug
          }
        }
        ... on SanityBlogPost {
          article {
            title
            _rawSummary(resolveReferences: { maxDepth: 10 })
            previewTitle
            _rawPreviewSummary(resolveReferences: { maxDepth: 10 })
            tags
            slug
          }
          heroImage {
            asset {
              gatsbyImageData
              url
            }
          }
        }
        ... on SanityPodcastPage {
          podcast {
            title
            _rawSummary(resolveReferences: { maxDepth: 10 })
            previewTitle
            _rawPreviewSummary(resolveReferences: { maxDepth: 10 })
            coverImage {
              asset {
                gatsbyImageData
                altText
                url
              }
            }
          }
        }
      }
      internal {
        type
      }
    }
    # this is to enable getting the publish date for the blog post - airtable is the source of truth
    airtableRecord: airtable(recordId: { eq: $airtableId }) {
      data {
        Publish_Date
        Last_Modified
      }
    }
  }
`

Template.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      htmlAst: PropTypes.object,
      frontmatter: PropTypes.shape({
        title: PropTypes.string,
        author: PropTypes.string,
        summary: PropTypes.string,
        keywords: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
        company: PropTypes.string,
        date: PropTypes.string,
        slug: PropTypes.string,
        imageAltText: PropTypes.string,
        image: PropTypes.shape({
          publicURL: PropTypes.string,
        }),
      }),
    }),
    sanityBlogPost: PropTypes.object,
    sanityPodcastPage: PropTypes.object,
  }).isRequired,
  pageContext: PropTypes.shape({
    selectedTag: PropTypes.string,
    canonicalTag: PropTypes.string,
  }),
}
